import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { Button, Select } from "@tellescope/design-system";
import type { HideUnsubmittedFormRule } from "../../../../../../shared/schema";
import { FormSelectOption } from "../../../FormSelectOption";
import { HideAfterInput } from "./HideAfterInput";
import { useHideUnsubmittedFormRuleRow } from "./useHideUnsubmittedFormRuleRow";

interface HideUnsubmittedFormRuleRowProps {
  rule: HideUnsubmittedFormRule;
  formIdsInOtherRules: string[];
  onChange: (rule: HideUnsubmittedFormRule) => void;
  onRemove: () => void;
}

export const HideUnsubmittedFormRuleRow = ({
  rule,
  formIdsInOtherRules,
  onChange,
  onRemove,
}: HideUnsubmittedFormRuleRowProps) => {
  const { t } = useTranslation();
  const { mode, options, searchQuery, setSearchQuery, isLoading, handleFormIdsChange, handleModeChange } =
    useHideUnsubmittedFormRuleRow(rule, formIdsInOtherRules, onChange);

  return (
    <div className="space-y-2 rounded border p-2">
      <div className="flex items-start gap-2">
        <Select multiple value={rule.formIds} onValueChange={handleFormIdsChange}>
          <Select.Trigger className="flex-1 min-w-0" aria-label={t("Forms")}>
            <Select.Value placeholder={t("Choose forms...")} />
          </Select.Trigger>
          <Select.Content
            filter="external"
            search={searchQuery}
            onSearchChange={setSearchQuery}
            loading={isLoading}
            searchPlaceholder={t("Search by name, description, or ID", { ns: "portal" })}
            emptyText={t("No forms found", { ns: "portal" })}
          >
            {options.map(({ form, label }) => (
              <Select.Item key={form.id} value={form.id} label={label} textValue={label}>
                <FormSelectOption title={label} description={form.description} formId={form.id} />
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onRemove}
          aria-label={t("Remove rule")}
          className="shrink-0"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={mode} onValueChange={handleModeChange}>
          <Select.Trigger className="w-32 shrink-0" aria-label={t("When to hide")}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="hide">{t("Hide after")}</Select.Item>
            <Select.Item value="never">{t("Never hide")}</Select.Item>
          </Select.Content>
        </Select>
        {rule.hideAfter && (
          <HideAfterInput value={rule.hideAfter} onChange={(hideAfter) => onChange({ ...rule, hideAfter })} />
        )}
      </div>
    </div>
  );
};
